import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, ArrowUpDown, Package, DollarSign, ArrowDownToLine, CalendarDays, ShoppingBag } from "lucide-react";

const COLS = [
  { key: "fecha", label: "Fecha" },
  { key: "codigo", label: "Codigo" },
  { key: "descripcion", label: "Descripcion" },
  { key: "cantidad", label: "Cant." },
  { key: "stock", label: "Stock" },
  { key: "pmenor", label: "PMenor" },
  { key: "pmayor", label: "PMayor" },
  { key: "destino", label: "Destino" },
  { key: "tipo_venta", label: "T.Venta" },
  { key: "cliente", label: "Cliente" },
  { key: "familia", label: "Familia" },
];

const formatPrice = (v) =>
  new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' Bs';

const formatDate = (d) => {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

const LocalSalidasModal = ({ open, onOpenChange, title, subtitle, entregas, tipoCambio, accentColor, icon: Icon }) => {
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "fecha", direction: "desc" });

  const tc = tipoCambio || 1;
  const formatUSD = (v) => '$ ' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v / tc);

  const filtered = useMemo(() => {
    if (!search) return entregas;
    const term = search.toLowerCase();
    return entregas.filter(e =>
      (e.codigo || '').toLowerCase().includes(term) ||
      (e.descripcion || '').toLowerCase().includes(term) ||
      (e.cliente || '').toLowerCase().includes(term)
    );
  }, [entregas, search]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key], bVal = b[sortConfig.key];
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? (aVal || '').localeCompare(bVal || '') : (bVal || '').localeCompare(aVal || '');
      return sortConfig.direction === 'asc' ? (aVal || 0) - (bVal || 0) : (bVal || 0) - (aVal || 0);
    });
  }, [filtered, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  // KPIs
  const kpis = useMemo(() => {
    const totalSalidas = entregas.length;
    const totalUnidades = entregas.reduce((s, e) => s + (e.cantidad || 0), 0);
    const valorPMayor = entregas.reduce((s, e) => s + (e.cantidad || 0) * (e.pmayor || 0), 0);
    const valorPMenor = entregas.reduce((s, e) => s + (e.cantidad || 0) * (e.pmenor || 0), 0);
    const clientesUnicos = new Set(entregas.map(e => e.cliente).filter(Boolean)).size;
    const familias = new Set(entregas.map(e => e.familia).filter(Boolean)).size;
    return { totalSalidas, totalUnidades, valorPMayor, valorPMenor, clientesUnicos, familias };
  }, [entregas]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-testid={`modal-local-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="heading-font text-lg flex items-center gap-2">
            {Icon && <Icon className={`h-5 w-5 ${accentColor}`} />}
            {title}
          </DialogTitle>
          {subtitle && <p className="body-font text-xs text-slate-500">{subtitle}</p>}
        </DialogHeader>

        {/* KPIs */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 flex-shrink-0" data-testid="modal-kpis">
          {[
            { label: "Salidas", value: kpis.totalSalidas.toLocaleString(), icon: Package },
            { label: "Unidades", value: kpis.totalUnidades.toLocaleString(), icon: ArrowDownToLine },
            { label: "Valor PMayor", value: formatPrice(kpis.valorPMayor), icon: DollarSign, usd: formatUSD(kpis.valorPMayor) },
            { label: "Valor PMenor", value: formatPrice(kpis.valorPMenor), icon: ShoppingBag, usd: formatUSD(kpis.valorPMenor) },
            { label: "Clientes", value: kpis.clientesUnicos, icon: CalendarDays },
            { label: "Familias", value: kpis.familias, icon: Package },
          ].map((k, i) => (
            <Card key={i} className="p-2.5 border border-slate-200" data-testid={`modal-kpi-${i}`}>
              <p className="body-font text-[10px] text-slate-500 uppercase tracking-wide">{k.label}</p>
              <p className="heading-font text-sm font-bold text-slate-900 mt-0.5">{k.value}</p>
              {k.usd && <p className="mono-font text-[10px] text-sky-700">{k.usd}</p>}
            </Card>
          ))}
        </div>

        {/* Search */}
        <div className="relative flex-shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por codigo, descripcion o cliente..."
            className="pl-10 text-xs h-9" data-testid="modal-search" />
        </div>

        {/* Count */}
        <p className="body-font text-xs text-slate-500 flex-shrink-0" data-testid="modal-count">
          Mostrando {filtered.length} de {entregas.length} registros
        </p>

        {/* Table */}
        <div className="flex-1 overflow-auto border border-slate-200 rounded-lg">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-50/95 backdrop-blur z-10">
              <TableRow>
                {COLS.map(col => (
                  <TableHead key={col.key} className="body-font font-semibold">
                    <Button variant="ghost" size="sm" onClick={() => handleSort(col.key)} className="text-[11px] px-1 h-7">
                      {col.label} <ArrowUpDown className="ml-1 h-2.5 w-2.5" />
                    </Button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={COLS.length} className="text-center py-6 text-xs text-slate-500">Sin registros</TableCell></TableRow>
              ) : sorted.map((e, i) => (
                <TableRow key={i} className="hover:bg-slate-50" data-testid={`modal-row-${i}`}>
                  <TableCell className="mono-font text-xs text-sky-700 font-medium">{formatDate(e.fecha)}</TableCell>
                  <TableCell className="mono-font text-xs font-medium">{e.codigo}</TableCell>
                  <TableCell className="body-font text-xs"><span className="block max-w-[220px] leading-snug line-clamp-2">{e.descripcion}</span></TableCell>
                  <TableCell className="mono-font text-xs text-center font-bold">{e.cantidad}</TableCell>
                  <TableCell className="mono-font text-xs text-center">{e.stock}</TableCell>
                  <TableCell className="mono-font text-xs">{formatPrice(e.pmenor)}</TableCell>
                  <TableCell className="mono-font text-xs">{formatPrice(e.pmayor)}</TableCell>
                  <TableCell className="body-font text-xs">{e.destino && <Badge variant="outline" className="text-[10px]">{e.destino}</Badge>}</TableCell>
                  <TableCell className="body-font text-xs">{e.tipo_venta && <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] border">{e.tipo_venta}</Badge>}</TableCell>
                  <TableCell className="body-font text-xs font-medium">{e.cliente}</TableCell>
                  <TableCell className="body-font text-xs text-slate-500">{e.familia}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LocalSalidasModal;
